import { useState } from "react"
import Modal from "./Modal"
import Icon from "../../components/Icon"
import { getApiErrorMessage } from "../../utils/errorMessage"

const inputClass =
  "w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"

function todayInputValue() {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

function ExpenseRecordModal({ providers = [], onCreate, onClose }) {
  const [form, setForm] = useState({
    providerId: "",
    description: "",
    date: todayInputValue(),
    amountPaid: "",
  })
  const [companyOpen, setCompanyOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const selectedProvider =
    providers.find((provider) => String(provider._id) === String(form.providerId)) || null

  const canSubmit =
    String(form.providerId || "").trim().length > 0 &&
    String(form.description || "").trim().length > 0 &&
    String(form.date || "").trim().length > 0 &&
    Number(form.amountPaid) > 0

  const submit = async (event) => {
    event.preventDefault()
    if (submitting || !canSubmit) return
    try {
      setSubmitting(true)
      setError("")
      await onCreate?.(form.providerId, {
        description: String(form.description || "").trim(),
        date: form.date,
        amountPaid: Number(form.amountPaid),
      })
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to add expense record."))
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Add expense"
      subtitle="Record supplier payment"
      icon="cash-coin"
      onClose={onClose}
      footer={
        <button
          type="submit"
          form="ut-expense-record-form"
          disabled={submitting || !canSubmit}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          <Icon name="plus-lg" size="0.8rem" />
          {submitting ? "Adding..." : "Add record"}
        </button>
      }
    >
      <form id="ut-expense-record-form" onSubmit={submit} className="space-y-4">
        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
            {error}
          </p>
        )}

        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Company</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => !submitting && providers.length > 0 && setCompanyOpen((open) => !open)}
              disabled={submitting || providers.length === 0}
              className={`flex w-full items-center justify-between gap-3 rounded-xl border bg-white px-3 py-2.5 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-teal-100 disabled:cursor-not-allowed disabled:bg-slate-50 ${
                form.providerId
                  ? "border-slate-300 text-slate-900 focus:border-teal-500"
                  : "border-amber-200 text-slate-400 focus:border-teal-500"
              }`}
              aria-haspopup="listbox"
              aria-expanded={companyOpen}
            >
              <span className="min-w-0 truncate">
                {selectedProvider?.name || (providers.length ? "Select utility company" : "No utility companies")}
              </span>
              <Icon name={companyOpen ? "chevron-up" : "chevron-down"} size="0.8rem" className="shrink-0 text-slate-400" />
            </button>

            {companyOpen && (
              <div
                role="listbox"
                className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl"
              >
                {providers.map((provider) => {
                  const active = String(provider._id) === String(form.providerId)
                  return (
                    <button
                      key={provider._id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        setForm((current) => ({ ...current, providerId: provider._id }))
                        setCompanyOpen(false)
                      }}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                        active ? "bg-teal-50 font-bold text-teal-800" : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span className="min-w-0 truncate">{provider.name || "Utility company"}</span>
                      {active && <Icon name="check-lg" size="0.8rem" className="shrink-0 text-teal-600" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
          {!form.providerId && (
            <p className="mt-1 text-[11px] font-medium text-amber-600">Choose the utility company this expense belongs to.</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Description</label>
          <textarea
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            rows="3"
            className={inputClass}
            placeholder="e.g. Water supplier payment"
            disabled={submitting}
            autoFocus
            required
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))}
              className={inputClass}
              disabled={submitting}
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Amount paid</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={form.amountPaid}
              onChange={(event) => setForm((current) => ({ ...current, amountPaid: event.target.value }))}
              className={inputClass}
              placeholder="0.00"
              inputMode="decimal"
              disabled={submitting}
              required
            />
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default ExpenseRecordModal
